import { useState, useCallback, useRef, useEffect } from 'react';

type ToastType = 'success' | 'error' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastState {
  message: string;
  type: ToastType;
  action?: ToastAction;
}

interface UseToastOptions {
  defaultDuration?: number;
  errorDuration?: number;
  actionDuration?: number;
}

type ShowToastParams = {
  message: string;
  type?: ToastType;
  action?: ToastAction;
};

interface UseToastReturn {
  toast: ToastState | null;
  showToast: (params: ShowToastParams | string, type?: ToastType) => void;
  hideToast: () => void;
}

/**
 * Hook for managing toast notifications
 */
export const useToast = (options: UseToastOptions = {}): UseToastReturn => {
  const {
    defaultDuration = 2000,
    errorDuration = 4000,
    actionDuration = 5000,
  } = options;

  const [toast, setToast] = useState<ToastState | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  const showToast = useCallback(
    (params: ShowToastParams | string, type: ToastType = 'success') => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Support both string and object params for backward compatibility
      const toastState: ToastState =
        typeof params === 'string'
          ? { message: params, type }
          : {
              message: params.message,
              type: params.type || type,
              action: params.action,
            };

      setToast(toastState);

      // Auto-hide after duration (longer for toasts with actions)
      let duration =
        toastState.type === 'error' ? errorDuration : defaultDuration;
      if (toastState.action) {
        duration = actionDuration;
      }
      timeoutRef.current = setTimeout(hideToast, duration);
    },
    [defaultDuration, errorDuration, actionDuration, hideToast]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { toast, showToast, hideToast };
};

export default useToast;
export type { ToastState, ToastType, ToastAction };
